import React, { useState, memo, useMemo, useCallback } from 'react'
import { Input, Dropdown, Button, Menu, Icon, Tooltip, Modal } from 'antd'
import SceneTransition from './SceneTransition'
import Shot from './Shot'
import {
  SortableContainer,
  SortableElement,
  SortableHandle
} from 'react-sortable-hoc'
import SceneAudioPreviewModal from '../SceneAudioPreviewModal'

const { TextArea } = Input

// Memoize the DragHandle
const DragHandle = memo(
  SortableHandle(() => (
    <span className="drag-handle" style={{ cursor: 'grab', marginRight: 8 }}>
      <Icon type="drag" />
    </span>
  ))
)

// Memoize individual Shot wrapper - BUT allow prompt updates
const SortableShot = memo(
  SortableElement(
    ({
      shot,
      displayIndex,
      sceneId,
      formData,
      setScenes,
      videoGenerating,
      transitions,
      handleRegenerateVideo,
      handleUploadVideo,
      handleEditSettings,
      handleTransitionSelect,
      handleApplyTransitionToAll,
      handleRemoveAllTransitions,
      isGeneratingAll,
      videoProvider,
      handleDeleteShot,
      updateShotDataRef,
      // Image generation props
      handleRegenerateImage,
      handleRegenerateWithReferenceNanoBanana,
      isImageGenerationLoading,
      videoId,
      isFetchingCharacters,
      shotViewMode = 'expanded'
    }) => (
      <Shot
        shot={shot}
        sceneId={sceneId}
        index={displayIndex}
        isLastShot={
          displayIndex ===
          formData.shotList.find(s => s.id === sceneId)?.shots.length - 1
        }
        formData={formData}
        setScenes={setScenes}
        videoGenerating={videoGenerating}
        transitions={transitions}
        handleRegenerateVideo={handleRegenerateVideo}
        handleUploadVideo={handleUploadVideo}
        handleEditSettings={handleEditSettings}
        handleTransitionSelect={handleTransitionSelect}
        handleApplyTransitionToAll={handleApplyTransitionToAll}
        handleRemoveAllTransitions={handleRemoveAllTransitions}
        isGeneratingAll={isGeneratingAll}
        videoProvider={videoProvider}
        handleDeleteShot={handleDeleteShot}
        DragHandle={DragHandle}
        updateShotDataRef={updateShotDataRef}
        // Image generation props
        handleRegenerateImage={handleRegenerateImage}
        handleRegenerateWithReferenceNanoBanana={
          handleRegenerateWithReferenceNanoBanana
        }
        isImageGenerationLoading={isImageGenerationLoading}
        videoId={videoId}
        isFetchingCharacters={isFetchingCharacters}
        shotViewMode={shotViewMode}
      />
    )
  ),
  (prevProps, nextProps) => {
    // Allow prompt updates, but still optimize other re-renders
    // Check if this specific shot's critical data changed
    const criticalDataChanged =
      prevProps.shot.vidSrc !== nextProps.shot.vidSrc ||
      prevProps.shot.imageSrc !== nextProps.shot.imageSrc ||
      prevProps.shot.is_static !== nextProps.shot.is_static ||
      prevProps.shot.video_source_image !==
        nextProps.shot.video_source_image ||
      prevProps.shot.isGenerating !== nextProps.shot.isGenerating ||
      prevProps.shot.errorGeneratingVideo !==
        nextProps.shot.errorGeneratingVideo ||
      prevProps.shot.progress !== nextProps.shot.progress ||
      prevProps.displayIndex !== nextProps.displayIndex ||
      prevProps.isGeneratingAll !== nextProps.isGeneratingAll ||
      prevProps.shot.volume !== nextProps.shot.volume ||
      prevProps.shot.videoEditTaskId !== nextProps.shot.videoEditTaskId ||
      prevProps.shot.videoChatHistory !== nextProps.shot.videoChatHistory ||
      prevProps.shot.videoEditProgress !== nextProps.shot.videoEditProgress ||
      prevProps.shot.video_duration !== nextProps.shot.video_duration

    // Check if prompts changed for THIS shot
    const promptsChanged =
      prevProps.shot.video_prompt !== nextProps.shot.video_prompt ||
      prevProps.shot.initial_image_prompt !==
        nextProps.shot.initial_image_prompt ||
      prevProps.shot.negative_prompt !== nextProps.shot.negative_prompt ||
      prevProps.shot.volume !== nextProps.shot.volume

    // Check if video generation status changed for THIS shot
    const prevGenerating = prevProps.videoGenerating.some(
      v => v.sceneId === prevProps.sceneId && v.shotId === prevProps.shot.id
    )
    const nextGenerating = nextProps.videoGenerating.some(
      v => v.sceneId === nextProps.sceneId && v.shotId === nextProps.shot.id
    )
    const generatingChanged = prevGenerating !== nextGenerating

    const transitionChanged = prevProps.transitions !== nextProps.transitions

    const isFetchingCharactersChanged =
      prevProps.isFetchingCharacters !== nextProps.isFetchingCharacters
    const shotViewModeChanged =
      prevProps.shotViewMode !== nextProps.shotViewMode

    // Check if relevant formData changed
    const formDataChanged =
      prevProps.formData.characterList !== nextProps.formData.characterList ||
      prevProps.formData.locationReference !==
        nextProps.formData.locationReference ||
      prevProps.formData.selectedCharacter !==
        nextProps.formData.selectedCharacter ||
      prevProps.formData.locationMap !== nextProps.formData.locationMap ||
      prevProps.formData.props_reference !==
        nextProps.formData.props_reference ||
      prevProps.formData.outfits_reference !==
        nextProps.formData.outfits_reference ||
      prevProps.formData.propMap !== nextProps.formData.propMap ||
      prevProps.formData.outfitMap !== nextProps.formData.outfitMap

    // Re-render if critical data, prompts, or generation status changed
    // Return true = skip render, false = do render
    return (
      !criticalDataChanged &&
      !promptsChanged &&
      !generatingChanged &&
      !transitionChanged &&
      !formDataChanged &&
      !isFetchingCharactersChanged &&
      !shotViewModeChanged
    )
  }
)

const SortableShotList = SortableContainer(
  ({
    shots,
    sceneId,
    formData,
    setScenes,
    videoGenerating,
    transitions,
    handleRegenerateVideo,
    handleUploadVideo,
    handleEditSettings,
    handleTransitionSelect,
    handleApplyTransitionToAll,
    handleRemoveAllTransitions,
    isGeneratingAll,
    videoProvider,
    handleDeleteShot,
    updateShotDataRef,
    // Image generation props
    handleRegenerateImage,
    handleRegenerateWithReferenceNanoBanana,
    isImageGenerationLoading,
    videoId,
    isFetchingCharacters,
    shotViewMode = 'expanded'
  }) => {
    return (
      <div className="shots-list">
        {shots.map((shot, index) => (
          <SortableShot
            key={shot.id}
            index={index}
            displayIndex={index}
            shot={shot}
            sceneId={sceneId}
            formData={formData}
            setScenes={setScenes}
            videoGenerating={videoGenerating}
            transitions={transitions}
            handleRegenerateVideo={handleRegenerateVideo}
            handleUploadVideo={handleUploadVideo}
            handleEditSettings={handleEditSettings}
            handleTransitionSelect={handleTransitionSelect}
            handleApplyTransitionToAll={handleApplyTransitionToAll}
            handleRemoveAllTransitions={handleRemoveAllTransitions}
            isGeneratingAll={isGeneratingAll}
            videoProvider={videoProvider}
            handleDeleteShot={handleDeleteShot}
            updateShotDataRef={updateShotDataRef}
            // Image generation props
            handleRegenerateImage={handleRegenerateImage}
            handleRegenerateWithReferenceNanoBanana={
              handleRegenerateWithReferenceNanoBanana
            }
            isImageGenerationLoading={isImageGenerationLoading}
            isFetchingCharacters={isFetchingCharacters}
            videoId={videoId}
            shotViewMode={shotViewMode}
          />
        ))}
      </div>
    )
  }
)

// Scene component abstracts a complete scene with its shots
const Scene = ({
  scene,
  transitions,
  formData,
  setScenes,
  isGeneratingAll = false,
  videoGenerating = [],
  handleRegenerateVideo,
  handleUploadVideo,
  handleEditSettings,
  handleTransitionSelect,
  handleApplyTransitionToAll,
  handleRemoveAllTransitions,
  videoProvider,
  onSortEnd,
  onPreviewScene,
  updateShotDataRef,
  handleDeleteShot,
  // Image generation props
  handleRegenerateImage,
  handleRegenerateWithReferenceNanoBanana,
  isImageGenerationLoading,
  videoId,
  isFetchingCharacters,
  shotViewMode = 'expanded'
}) => {
  const lastScene = formData.shotList[formData.shotList.length - 1]
  const isLastScene = lastScene && lastScene.id === scene.id

  const firstScene = formData.shotList[0]
  const isFirstScene = firstScene && firstScene.id === scene.id

  // canPreview: each shot must have either a video clip or a static image.
  const canPreview = useMemo(() => {
    return (
      scene.shots &&
      scene.shots.length > 0 &&
      scene.shots.every(
        shot => shot && (shot.vidSrc || (shot.is_static && shot.imageSrc))
      )
    )
  }, [
    scene.shots
      .map(s => `${s?.vidSrc || ''}:${s?.is_static ? s?.imageSrc || '' : ''}`)
      .join(',')
  ])

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)
  const [isAudioPreviewOpen, setIsAudioPreviewOpen] = useState(false)
  const hasSceneAudio = Boolean(scene?.audio_link)

  const handleConfirmPreview = useCallback(() => {
    onPreviewScene && onPreviewScene(scene.id)
    setIsPreviewModalOpen(false)
  }, [onPreviewScene, scene.id])

  const handlePreviewClick = useCallback(() => {
    if (!canPreview) {
      Modal.info({
        title: 'Cannot preview scene',
        content:
          'All non-static shots must have a video source to preview. Static shots need an image.'
      })
      return
    }

    setIsPreviewModalOpen(true)
  }, [canPreview])

  return (
    <>
      <div className={`scene-container scene-container--${shotViewMode}`}>
        <div className="left-panel">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <div className="scene-header-actions">
              <h2 style={{ margin: 0, marginRight: 8 }}>
                {scene.title} ({scene.shots && scene.shots.length} shots)
              </h2>

              <Tooltip
                title={
                  scene.shots.length < 2
                    ? 'Scene must have at least 2 shots to preview.'
                    : isGeneratingAll || videoGenerating.includes(scene.id)
                    ? 'Scene is currently generating'
                    : canPreview
                    ? 'Preview scene'
                    : 'All shots must have a video source to preview'
                }
              >
                <Button
                  className="scene-header-actions__btn"
                  type="primary"
                  size="small"
                  onClick={handlePreviewClick}
                  disabled={
                    !canPreview ||
                    isGeneratingAll ||
                    videoGenerating.includes(scene.id) ||
                    scene.shots.length < 2
                  }
                  style={{
                    height: 32,
                    marginLeft: 4,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Icon type="eye" />
                  <span style={{ marginLeft: 6, fontSize: 12 }}>Preview</span>
                </Button>
              </Tooltip>

              <Tooltip
                title={
                  hasSceneAudio
                    ? 'Listen to generated scene audio'
                    : 'Scene audio not available yet'
                }
              >
                <Button
                  className="scene-header-actions__btn scene-header-actions__btn--audio"
                  size="small"
                  onClick={() => setIsAudioPreviewOpen(true)}
                  disabled={!hasSceneAudio}
                  style={{
                    height: 32,
                    marginLeft: 4,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Icon type="sound" />
                  <span style={{ marginLeft: 6, fontSize: 12 }}>Audio</span>
                </Button>
              </Tooltip>

              {isFirstScene && shotViewMode === 'compact' && (
                <SceneTransition
                  sceneId={scene.id}
                  position="start"
                  transitionType={transitions[`${scene.id}-start`]}
                  handleTransitionSelect={handleTransitionSelect}
                  handleApplyTransitionToAll={handleApplyTransitionToAll}
                  handleRemoveAllTransitions={handleRemoveAllTransitions}
                  compact
                  compactLabel="Start"
                />
              )}
            </div>
          </div>
          <TextArea
            value={scene.description}
            rows={2}
            disabled={isGeneratingAll || videoGenerating.includes(scene.id)}
            onChange={e => {
              const newDescription = e.target.value
              setScenes(prevScenes =>
                prevScenes.map(s =>
                  s.id === scene.id ? { ...s, description: newDescription } : s
                )
              )
            }}
          />
        </div>

        <div className="right-panel">
          <div className="shots-grid">
            {isFirstScene && shotViewMode !== 'compact' && (
              <SceneTransition
                sceneId={scene.id}
                position="start"
                transitionType={transitions[`${scene.id}-start`]}
                handleTransitionSelect={handleTransitionSelect}
                handleApplyTransitionToAll={handleApplyTransitionToAll}
                handleRemoveAllTransitions={handleRemoveAllTransitions}
              />
            )}
            <SortableShotList
              axis="x"
              shots={scene.shots}
              sceneId={scene.id}
              formData={formData}
              setScenes={setScenes}
              videoGenerating={videoGenerating}
              transitions={transitions}
              handleRegenerateVideo={handleRegenerateVideo}
              handleUploadVideo={handleUploadVideo}
              handleEditSettings={handleEditSettings}
              handleTransitionSelect={handleTransitionSelect}
              handleApplyTransitionToAll={handleApplyTransitionToAll}
              handleRemoveAllTransitions={handleRemoveAllTransitions}
              isGeneratingAll={isGeneratingAll}
              videoProvider={videoProvider}
              updateShotDataRef={updateShotDataRef}
              handleDeleteShot={handleDeleteShot}
              // Image generation props
              handleRegenerateImage={handleRegenerateImage}
              handleRegenerateWithReferenceNanoBanana={
                handleRegenerateWithReferenceNanoBanana
              }
              isImageGenerationLoading={isImageGenerationLoading}
              videoId={videoId}
              useDragHandle
              onSortEnd={onSortEnd}
              distance={1}
              helperClass="sortable-helper"
              isFetchingCharacters={isFetchingCharacters}
              shotViewMode={shotViewMode}
            />

            {/* <SceneTransition
              sceneId={scene.id}
              position="end"
              transitionType={transitions[`${scene.id}-end`]}
              handleTransitionSelect={handleTransitionSelect}
            /> */}
          </div>
        </div>
      </div>

      <Modal
        maskClosable={false}
        title="Confirm Scene Preview"
        visible={isPreviewModalOpen}
        onOk={handleConfirmPreview}
        onCancel={() => setIsPreviewModalOpen(false)}
        okText="Continue"
        cancelText="Cancel"
        centered
      >
        <p>
          This will start the preview generation process for this scene. It will
          only contain the shots inside this scene. Are you sure you want to
          proceed?
        </p>
      </Modal>

      <SceneAudioPreviewModal
        visible={isAudioPreviewOpen}
        audioUrl={scene?.audio_link}
        sceneTitle={scene?.title}
        onClose={() => setIsAudioPreviewOpen(false)}
      />
    </>
  )
}

export default Scene
